// Shared constants for the tRPC → OpenAPI → CLI/MCP codegen pipeline (INF-4438).
//
// The vendor-extension keys are the contract between the spec generator (which
// writes them onto each operation) and the CLI/MCP generators (which read them);
// the auth values and the generated-tool prefix are closed sets used across all
// three generators. They live here in the leaf models package so every consumer
// imports one definition instead of re-spelling the literals — a single edit
// site, and no drift between the writer and the readers.

import { z } from "zod";

import { PAGINATION_MODES } from "./pagination.models";

/** Dotted tRPC procedure path (e.g. `project.getProject`) for an operation. */
export const OPENAPI_TRPC_PATH_KEY = "x-trpc-path" as const;
/** Whether the backing tRPC procedure is a `query` or a `mutation`. */
export const OPENAPI_TRPC_TYPE_KEY = "x-trpc-type" as const;
/** Which credentials the operation accepts (see {@link PROCEDURE_AUTH}). */
export const OPENAPI_AUTH_KEY = "x-inf-auth" as const;
/**
 * Pagination descriptor for a list operation (see {@link paginationHintSchema}).
 * The spec generator copies it from the procedure's `meta.openapi.pagination`;
 * the CLI generator reads it to drive the interactive pager instead of dumping
 * the raw array. Its presence is the opt-in to the pager.
 */
export const OPENAPI_PAGINATION_KEY = "x-pagination" as const;
/**
 * Marks an operation as excluded from the MCP tool surface. The spec generator
 * copies it from the procedure's `meta.openapi.mcpExclude`; the MCP generator
 * skips any operation carrying `true`, while the CLI and REST surfaces still
 * include it. This is how sensitive endpoints (secrets, capability URLs, raw
 * customer content/PII, destructive super-admin mutations) reach the
 * human-operated CLI/REST surfaces without becoming LLM-invocable MCP tools.
 */
export const OPENAPI_MCP_EXCLUDE_KEY = "x-mcp-exclude" as const;

/**
 * Marks an operation as CLI-only: it is generated as an `inf` CLI command
 * (which calls tRPC directly) but kept off both the public REST surface and the
 * LLM-invocable MCP tool surface. The spec generator stamps this from a
 * side-list of tRPC paths (kept out of the procedure `meta` type to avoid
 * re-expanding it across every procedure) and also stamps
 * {@link OPENAPI_MCP_EXCLUDE_KEY} so the MCP generator's existing skip applies;
 * the REST handler 404s any request whose matched operation carries this. This
 * is how irreversible cross-tenant super-admin mutations stay reachable only
 * from the human-operated CLI, never as a public REST endpoint or an
 * LLM-invocable tool.
 */
export const OPENAPI_CLI_ONLY_KEY = "x-cli-only" as const;

/**
 * Marks an operation as super-admin / platform-admin surface. The spec
 * generator stamps it on every operation {@link isAdminOperation} classifies as
 * admin, and on every procedure built on a super-admin base, then writes those
 * operations to `openapi-cli-only.json` instead of the published `openapi.json`
 * (so they never reach the public docs or the MCP tools) and stamps
 * {@link OPENAPI_MCP_EXCLUDE_KEY}. Unlike {@link OPENAPI_CLI_ONLY_KEY} it does
 * not block REST: super-admin scripts still call these over `/api/rest`, where
 * the procedure's own auth gate applies. The CLI generator files them under
 * the hidden `inf admin` group.
 */
export const OPENAPI_ADMIN_KEY = "x-admin" as const;

/**
 * Marks an operation as destructive, so the generated `inf` CLI command adds a
 * `-y`/`--yes` flag and prompts for confirmation before invoking (refusing in a
 * non-interactive shell unless `--yes` is passed). Stamped from a side-list of
 * tRPC paths, like {@link OPENAPI_CLI_ONLY_KEY}. Orthogonal to CLI-only — a
 * destructive op need not be CLI-only — though the irreversible cross-tenant
 * super-admin mutations are currently both.
 */
export const OPENAPI_DESTRUCTIVE_KEY = "x-destructive" as const;

/**
 * How a list operation paginates, so a single generic client (the CLI pager)
 * can drive any list without per-endpoint code. It names the wire fields rather
 * than assuming them, because the exposed lists disagree: the item array is
 * `items` on some and `conversations`/`schedules`/`events` on Halo, and the next
 * page is reached by an `offset` on the standardized lists but an opaque
 * `cursor` on the high-volume ClickHouse reads.
 *
 * Discriminated on `mode`:
 * - `offset` — random access; `offsetField`/`totalField` let the client jump to
 *   any page and know the total.
 * - `cursor` — forward-only; `cursorRequestParam`/`cursorResponseField` carry the
 *   opaque cursor echoed back to fetch the next page.
 */
/**
 * How a sortable list takes its ordering, so the pager can offer a sort cycle.
 * The exposed lists disagree on the wire encoding, so this is discriminated on
 * `style`:
 * - `split` — two input fields, a key field (`sortBy`) and a direction field
 *   (`sortOrder`). Used by the cursor/ClickHouse reads.
 * - `object` — a single input field carrying a `{ key, direction }` object.
 *   Used by the offset/DataTable reads (`sortSchema`).
 * Both name the closed set of keys the pager may cycle through.
 */
export const paginationSortHintSchema = z.discriminatedUnion("style", [
  z.object({
    style: z.literal("split"),
    /** Input field carrying the sort key. */
    keyParam: z.string(),
    /** Input field carrying the direction ("asc"/"desc"). */
    orderParam: z.string(),
    /** The keys the pager cycles through. */
    keys: z.array(z.string()).min(1),
  }),
  z.object({
    style: z.literal("object"),
    /** Input field carrying the `{ key, direction }` sort object. */
    param: z.string(),
    /** The keys the pager cycles through. */
    keys: z.array(z.string()).min(1),
  }),
]);

export type PaginationSortHint = z.infer<typeof paginationSortHintSchema>;

export const paginationHintSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal(PAGINATION_MODES[0]), // "offset"
    /** Input field carrying the page size. */
    limitParam: z.string(),
    /** Input field carrying the row offset. */
    offsetParam: z.string(),
    /** Response field holding the item array. */
    itemsField: z.string(),
    /** Response field holding the pre-page total-matching count. */
    totalField: z.string(),
    /** How to sort, if the list is sortable. */
    sort: paginationSortHintSchema.optional(),
    /** Input field carrying a free-text search string, if the list is searchable. */
    searchParam: z.string().optional(),
  }),
  z.object({
    mode: z.literal(PAGINATION_MODES[1]), // "cursor"
    /** Input field carrying the page size. */
    limitParam: z.string(),
    /** Input field carrying the opaque cursor from a prior page. */
    cursorRequestParam: z.string(),
    /** Response field holding the item array. */
    itemsField: z.string(),
    /** Response field holding the opaque next cursor (null on the last page). */
    cursorResponseField: z.string(),
    /** How to sort, if the list is sortable. */
    sort: paginationSortHintSchema.optional(),
    /** Input field carrying a free-text search string, if the list is searchable. */
    searchParam: z.string().optional(),
  }),
]);

export type PaginationHint = z.infer<typeof paginationHintSchema>;

/**
 * Which credentials a procedure accepts. Derived from the procedure base and
 * merged into every procedure's meta; the generators branch on it (the CLI emits
 * a session-only auth gate, the MCP server omits session-only procedures).
 */
export const PROCEDURE_AUTH = {
  SESSION: "session",
  SESSION_OR_API_KEY: "session-or-api-key",
} as const;

export type ProcedureAuth =
  (typeof PROCEDURE_AUTH)[keyof typeof PROCEDURE_AUTH];

/** Name prefix for auto-generated MCP tools, keeping them clear of hand-written ones. */
export const GENERATED_MCP_TOOL_PREFIX = "gen_";

// Resources (first REST path segment, trailing `s` dropped) that are admin-only
// wholesale. account-deletion is NOT listed: `/account-deletion/*` is the
// user-facing delete-my-account flow, and its two super-admin ops
// (`/account-deletions/admin-*`) are caught by the summary marker instead.
const ADMIN_RESOURCES = new Set([
  "admin-telemetry",
  "pricing-backfill",
  "superadmin",
]);
const ADMIN_TAGS = new Set(["Super Admin", "Superadmin", "Billing Admin"]);
// Every prose convention the API uses for admin-gated procedures:
// "super-admin"/"superadmin", "platform admin"/"platform-admin" (modelAdmin
// catalog ops, weights uploads, recipe administration), and "super access"
// (eval-run logs). "platform-default" must NOT match — that is a team read.
const ADMIN_SUMMARY_RE = /super-?admin|platform[- ]admin|super access/i;
// Routers that are admin-gated wholesale; their ops are admin even when a
// summary forgets the marker (e.g. engine-config history/delete).
const ADMIN_TRPC_PREFIXES = ["modelAdmin.", "weightsUpload."];
// Procedure-name convention for admin variants on otherwise-public routers
// (starterTemplate.listForAdmin / getTemplateForAdmin).
const ADMIN_TRPC_RE = /foradmin/i;

/**
 * Whether an OpenAPI operation is super-admin surface by the API's naming
 * conventions: its whole resource is admin-only, its summary carries an admin
 * marker, it has an admin-only tag, or its tRPC path is on an admin router or
 * names an admin variant. The spec generator and the CLI generator share this
 * one classification.
 */
export function isAdminOperation(operation: {
  path: string;
  summary: string;
  tags: readonly string[];
  trpcPath: string;
}): boolean {
  const resource = (operation.path.split("/").find(Boolean) ?? "").replace(
    /s$/,
    "",
  );
  return (
    ADMIN_RESOURCES.has(resource) ||
    ADMIN_SUMMARY_RE.test(operation.summary) ||
    operation.tags.some((tag) => ADMIN_TAGS.has(tag)) ||
    ADMIN_TRPC_PREFIXES.some((prefix) =>
      operation.trpcPath.startsWith(prefix),
    ) ||
    ADMIN_TRPC_RE.test(operation.trpcPath)
  );
}
